import { ChevronLeft, ChevronRight } from "lucide-react";
import { Button } from "./button";

interface PaginationProps {
  currentPage: number;
  totalPages: number;
  onPageChange: (page: number) => void;
  maxVisible?: number;
}

export function Pagination({ currentPage, totalPages, onPageChange, maxVisible = 5 }: PaginationProps) {
  // Fonction pour générer les numéros de page à afficher
  const getPageNumbers = () => {
    const pageNumbers = [];
    
    if (totalPages <= maxVisible) {
      // Si le nombre total de pages est inférieur ou égal au nombre maximum de pages visibles,
      // afficher toutes les pages
      for (let i = 1; i <= totalPages; i++) {
        pageNumbers.push(i);
      }
    } else {
      // Sinon, afficher un sous-ensemble de pages avec des ellipses
      const halfVisible = Math.floor(maxVisible / 2);
      
      // Toujours afficher la première page
      pageNumbers.push(1);
      
      // Déterminer la plage de pages à afficher autour de la page courante
      let startPage = Math.max(2, currentPage - halfVisible);
      let endPage = Math.min(totalPages - 1, currentPage + halfVisible);
      
      // Ajuster la plage si nécessaire pour maintenir le nombre maximum de pages visibles
      if (startPage <= 2) {
        endPage = Math.min(totalPages - 1, maxVisible - 1);
      }
      if (endPage >= totalPages - 1) {
        startPage = Math.max(2, totalPages - maxVisible + 1);
      }
      
      // Ajouter une ellipse si nécessaire avant la plage
      if (startPage > 2) {
        pageNumbers.push('...');
      }
      
      // Ajouter les pages dans la plage
      for (let i = startPage; i <= endPage; i++) {
        pageNumbers.push(i);
      }
      
      // Ajouter une ellipse si nécessaire après la plage
      if (endPage < totalPages - 1) {
        pageNumbers.push('...');
      }
      
      // Toujours afficher la dernière page
      if (totalPages > 1) {
        pageNumbers.push(totalPages);
      }
    }
    
    return pageNumbers;
  };

  const pageNumbers = getPageNumbers();

  return (
    <div className="flex items-center justify-between px-2 py-4">
      <div className="flex w-full justify-between sm:hidden">
        <Button
          variant="outline"
          className="flex items-center gap-1 border-white/10 bg-white/5 text-white hover:bg-white/10"
          onClick={() => onPageChange(currentPage - 1)}
          disabled={currentPage === 0}
        >
          <ChevronLeft className="h-4 w-4" />
          Précédent
        </Button>
        <Button
          variant="outline"
          className="flex items-center gap-1 border-white/10 bg-white/5 text-white hover:bg-white/10"
          onClick={() => onPageChange(currentPage + 1)}
          disabled={currentPage === totalPages - 1}
        >
          Suivant
          <ChevronRight className="h-4 w-4" />
        </Button>
      </div>
      <div className="hidden sm:flex sm:flex-1 sm:items-center sm:justify-between">
        <div>
          <p className="text-sm text-gray-400">
            Page <span className="font-medium text-white">{currentPage + 1}</span> sur{" "}
            <span className="font-medium text-white">{totalPages}</span>
          </p>
        </div>
        <div>
          <nav className="isolate inline-flex -space-x-px rounded-md shadow-sm">
            <Button
              variant="outline"
              className="flex items-center gap-1 rounded-l-md border-white/10 bg-white/5 text-white hover:bg-white/10"
              onClick={() => onPageChange(currentPage - 1)}
              disabled={currentPage === 0}
            >
              <ChevronLeft className="h-4 w-4" />
              Précédent
            </Button>
            
            {pageNumbers.map((page, index) => (
              page === '...' ? (
                <Button
                  key={`ellipsis-${index}`}
                  variant="outline"
                  className="border-white/10 bg-white/5 text-white"
                  disabled
                >
                  ...
                </Button>
              ) : (
                <Button
                  key={`page-${page}`}
                  variant="outline"
                  className={`border-white/10 ${
                    page === currentPage + 1
                      ? "bg-yellow-400/20 text-yellow-400"
                      : "bg-white/5 text-white hover:bg-white/10"
                  }`}
                  onClick={() => onPageChange(Number(page) - 1)}
                >
                  {page}
                </Button>
              )
            ))}
            
            <Button
              variant="outline"
              className="flex items-center gap-1 rounded-r-md border-white/10 bg-white/5 text-white hover:bg-white/10"
              onClick={() => onPageChange(currentPage + 1)}
              disabled={currentPage === totalPages - 1}
            >
              Suivant
              <ChevronRight className="h-4 w-4" />
            </Button>
          </nav>
        </div>
      </div>
    </div>
  );
}
